import React, { PropsWithChildren, useCallback, useEffect, useRef } from "react"
import { SearchForm } from "../SearchForm"
import clsx from "classnames"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { NavItems } from "./NavItems"
import { RegionDropdown } from "./RegionDropdown"
import { ActionButtons } from "./ActionButtons"
import { isElementVisible } from "common/lib/isElementVisible"
import { X } from "lucide-react"

interface MobileMenuProps {
  isActive: boolean
  onClose: () => void
}

const FOCUSABLE_SELECTORS =
  'a[href], button:not(:disabled), select:not(:disabled), textarea:not(:disabled), input[type="text"]:not(:disabled), input[type="radio"]:not(:disabled), input[type="checkbox"]:not(:disabled), input[type="search"]:not(:disabled)'

const getFocusableElements = (container: HTMLElement) =>
  Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS)).filter(
    isElementVisible,
  ) as HTMLElement[]

const useFocusTrap = (
  isActive: boolean,
  containerRef: React.RefObject<HTMLElement>,
  onClose: () => void,
) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isActive || !containerRef.current) return

      const focusableElements = getFocusableElements(containerRef.current)
      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.key === "Escape") {
        event.preventDefault()
        const searchInput = containerRef.current.querySelector(
          'input[type="search"]',
        ) as HTMLInputElement
        if (searchInput && searchInput.value !== "" && document.activeElement === searchInput) {
          searchInput.value = ""
          searchInput.focus()
        } else {
          onClose()
        }
        return
      }

      if (event.key !== "Tab") return

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    },
    [containerRef, isActive, onClose],
  )

  useEffect(() => {
    const menuElement = containerRef.current
    if (!isActive || !menuElement) return

    // Wait a little before trying to focus the first element to prevent focusing before it has time to render properly
    const timeoutId = setTimeout(() => {
      const focusableElements = getFocusableElements(menuElement)
      if (focusableElements.length !== 0) {
        focusableElements[0].focus()
      }
    }, 50)

    menuElement.addEventListener("keydown", handleKeyDown)
    return () => {
      clearTimeout(timeoutId)
      if (handleKeyDown) menuElement.removeEventListener("keydown", handleKeyDown)
    }
  }, [isActive, containerRef, onClose, handleKeyDown])
}

export const MobileMenu = ({ isActive, onClose }: PropsWithChildren<MobileMenuProps>) => {
  const t = useTranslations("layout.header")
  const menuRef = useRef<HTMLElement>(null)

  useFocusTrap(isActive, menuRef, onClose)

  return (
    <section
      ref={menuRef}
      id="mobile-menu-container"
      className={clsx(
        "absolute top-px mt-10 w-full bg-white text-gray-800 transition-all duration-500 lg:hidden",
        {
          "visible z-40 h-svh pb-8": isActive,
          "invisible h-0": !isActive,
        },
      )}
      data-testid="mobileMenu"
      aria-modal={isActive ? true : undefined}
      aria-label={t("menu")}
      role="dialog"
    >
      {/* Role document is required here so screen readers understand what is part of the dialog */}
      <div role="document" className="h-full">
        {/* We use isActive to make it instantly disappear so that it doesn't overlap topbar during closing transition */}
        {isActive && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t("close")}
            className="-mt-10 block p-2 text-gray-800 -outline-offset-1 hover:bg-gray-50"
            aria-controls="mobile-menu-container"
            aria-expanded={true}
          >
            <X className="size-6" />
          </button>
        )}

        <div className="h-full overflow-y-auto md:max-w-screen">
          <div className="mx-1">
            <SearchForm />
          </div>

          <NavItems />
          <RegionDropdown />
          <ActionButtons />
        </div>
      </div>
    </section>
  )
}
